/**
 * As áreas do Easy HUB, num lugar só.
 *
 * Antes desta lista o mesmo union estava escrito à mão em quatro arquivos
 * (store, seletor, sidebar e NavigationRoutes) e o conjunto de telas de shell
 * escuro em três. O TypeScript só reclama onde os valores se encontram, então
 * dava para atualizar dois e deixar dois desatualizados sem o build acusar —
 * e o sintoma aparecia em produção como item de menu sumido ou logo errada.
 *
 * O HUB não entra aqui: ele é a antessala, acima das áreas, e não tem navbar.
 */

export type Workspace = 'crm' | 'service-desk' | 'project' | 'monitor' | 'ai-agents'

export interface WorkspaceDef {
  key: Workspace
  /** Chave i18n do rótulo. */
  labelKey: string
  /** Ícone Material usado no seletor. */
  icon: string
  /** Rota que o seletor abre ao escolher a área. */
  home: string
  /** Texto da marca no topo, em caixa alta. Ver AreaBrand.vue. */
  brand: string
}

// A ORDEM aqui é a ordem do seletor, e foi pedida assim: CRM, Service Desk,
// Project, AI Agents, Monitoring.
export const WORKSPACES: WorkspaceDef[] = [
  { key: 'crm', labelKey: 'workspace.crm', icon: 'dashboard', home: 'map', brand: 'CRM' },
  {
    key: 'service-desk',
    labelKey: 'workspace.serviceDesk',
    icon: 'support_agent',
    home: 'sd-home',
    brand: 'SERVICE DESK',
  },
  // Easy Project: o que é tocar projeto e implantação saiu do Service Desk e
  // veio para cá — lá ficou o que é atendimento (ticket, SLA, escala).
  { key: 'project', labelKey: 'workspace.project', icon: 'account_tree', home: 'project-home', brand: 'PROJECT' },
  { key: 'ai-agents', labelKey: 'workspace.aiAgents', icon: 'smart_toy', home: 'ai-home', brand: 'AI AGENTS' },
  // Easy Monitoring: monitorar as aplicações. Só isso — os indicadores de
  // negócio moram no CRM, que é a ferramenta a que eles pertencem.
  { key: 'monitor', labelKey: 'workspace.monitor', icon: 'monitor_heart', home: 'monitoring', brand: 'MONITORING' },
]

export const WORKSPACE_KEYS = WORKSPACES.map((w) => w.key)

/**
 * Chaves que já foram gravadas no localStorage de quem usa o app e não existem
 * mais. Sem este mapa, normalizeWorkspace jogaria essa gente em 'crm' no
 * primeiro acesso depois do deploy — silenciosamente, porque o valor
 * desconhecido cai no default.
 */
const LEGACY: Record<string, Workspace> = {
  // Indicadores nunca foi ferramenta: é tela do CRM. Quem tinha essa chave
  // salva vai para o CRM, não para o Monitor, onde a tela nem aparece.
  indicators: 'crm',
  monitoring: 'monitor',
  // 'panels' existiu por poucas horas, entre a fusão das duas áreas e esta
  // correção. Quem abriu o app nesse meio-tempo tem o valor gravado.
  panels: 'monitor',
  // 'hub' veio da ECRM-176, que foi revertida na CRM-41.
  hub: 'crm',
}

/**
 * Permissões que decidem se uma área pode ser aberta.
 *
 * Só 'ai-agents' é fechada hoje — é módulo interno, liberado por papel. As
 * outras três são abertas a quem está logado.
 */
export interface WorkspacePermissions {
  /** O usuário tem pelo menos papel de leitor no Easy AI Agents. */
  aiAgents: boolean
}

export function canAccessWorkspace(key: Workspace, perms: WorkspacePermissions): boolean {
  if (key === 'ai-agents') return perms.aiAgents
  return true
}

/**
 * A área que a interface deve MOSTRAR, dado o que está salvo e o que a pessoa
 * pode ver.
 *
 * normalizeWorkspace valida a CHAVE; esta valida a PERMISSÃO. Sem ela, quem
 * perde o papel de AI Agents fica com 'ai-agents' gravado no navegador e a tela
 * se contradiz: a sidebar filtra os itens por papel e fica vazia, o seletor não
 * acha a área na lista já filtrada e exibe CRM, e a navbar lê a lista completa e
 * estampa AI AGENTS. Três componentes, três respostas.
 *
 * A gravação não tem como fazer essa conta: o store nasce antes do /users/me
 * responder. Por isso a correção é na leitura, e é aqui — num lugar só, para os
 * três concordarem.
 */
export function effectiveWorkspace(stored: unknown, perms: WorkspacePermissions): Workspace {
  const key = normalizeWorkspace(stored)
  return canAccessWorkspace(key, perms) ? key : 'crm'
}

export function normalizeWorkspace(value: unknown): Workspace {
  if (typeof value === 'string') {
    if (WORKSPACE_KEYS.includes(value as Workspace)) return value as Workspace
    if (LEGACY[value]) return LEGACY[value]
  }
  return 'crm'
}

/**
 * Área a que um path pertence. É o que mantém o seletor em sincronia com a URL
 * em deep link, reload e voltar/avançar.
 */
export function workspaceForPath(path: string): Workspace {
  if (path.startsWith('/service-desk')) return 'service-desk'
  if (path.startsWith('/project')) return 'project'
  if (path.startsWith('/ai-agents')) return 'ai-agents'
  if (path.startsWith('/monitoring')) return 'monitor'
  // /crm/... é explícito desde que o CRM ganhou prefixo próprio. O default
  // continua sendo 'crm' porque sobra o que não é de ferramenta nenhuma
  // (/preferences, caminhos legados ainda não redirecionados) — e o CRM é o
  // lugar menos surpreendente para cair.
  return 'crm'
}
